<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu 
      :user="user" 
      :isMenuOpen="isMenuOpen" 
      @toggle-menu="toggleMenu" 
      @close-menu="closeMenu" 
      @logout="logout"
    />

    <!-- Contenido Principal - ajustado para tener margen izquierdo en escritorio -->
    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold mb-6">Registro de Prospectos</h1>
          
          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
          
          <div class="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            <p v-if="adviserName">
              Si tienes dudas o comentarios, no dudes en contactarte con tu asesor, {{ adviserName }}.
            </p>
            <p v-else>
              Si tienes dudas o comentarios, no dudes en contactarte con tu asesor.
            </p>
          </div>
          
          <div v-if="successMessage" class="hidden bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {{ successMessage }}
          </div>
          
          <div v-if="errorMessage" class="hidden bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ errorMessage }}
          </div>
          
          <form @submit.prevent="submitForm" class="mt-6">
            <!-- Datos personales -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
              <h2 class="text-lg font-semibold mb-4">Datos personales</h2>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="nombre" class="block text-sm font-medium text-gray-500">Nombre*</label>
                  <input 
                    id="nombre" 
                    v-model="formData.Names" 
                    type="text" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label for="apellidoPaterno" class="block text-sm font-medium text-gray-500">Apellido Paterno*</label>
                  <input 
                    id="apellidoPaterno" 
                    v-model="formData.FatherLastName" 
                    type="text" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label for="apellidoMaterno" class="block text-sm font-medium text-gray-500">Apellido Materno*</label>
                  <input 
                    id="apellidoMaterno" 
                    v-model="formData.MotherLastName" 
                    type="text" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div class="hidden">
                  <label for="telefonoFijo" class="block text-sm font-medium text-gray-500">Teléfono Fijo</label>
                  <input 
                    id="telefonoFijo" 
                    v-model="formData.Phone" 
                    type="tel" 
                    maxlength="10"
                    pattern="[0-9]{10}"
                    @input="validatePhoneInput('Phone')"
                    @blur="validatePhone('Phone')"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    :class="{ 'border-red-500': phoneErrors.Phone }"
                  />
                  <p v-if="phoneErrors.Phone" class="mt-1 text-sm text-red-600">{{ phoneErrors.Phone }}</p>
                </div>
                

                <div>
                  <label for="telefonoMovil" class="block text-sm font-medium text-gray-500">Teléfono Móvil*</label>
                  <input 
                    id="telefonoMovil" 
                    v-model="formData.Mobile" 
                    type="tel" 
                    maxlength="10"
                    pattern="[0-9]{10}"
                    required
                    @input="validatePhoneInput('Mobile')"
                    @blur="validatePhone('Mobile')"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    :class="{ 'border-red-500': phoneErrors.Mobile }"
                  />
                  <p v-if="phoneErrors.Mobile" class="mt-1 text-sm text-red-600">{{ phoneErrors.Mobile }}</p>
                </div>
                

                <div>
                  <label for="email" class="block text-sm font-medium text-gray-500">Email</label>
                  <input 
                    id="email" 
                    v-model="formData.Email" 
                    type="email"
                    @blur="validateEmail"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    :class="{ 'border-red-500': emailError }"
                  />
                  <p v-if="emailError" class="mt-1 text-sm text-red-600">{{ emailError }}</p>
                </div>
                
                <div>
                  <label for="fechaNacimiento" class="block text-sm font-medium text-gray-500">Fecha de Nacimiento*</label>
                  <input 
                    id="fechaNacimiento" 
                    v-model="formData.Birthdate" 
                    type="date" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label for="curp" class="block text-sm font-medium text-gray-500">CURP*</label>
                  <input 
                    id="curp" 
                    v-model="formData.Curp" 
                    type="text" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-500">Sexo*</label>
                  <div class="mt-2 space-x-6 flex">
                    <div class="flex items-center">
                      <input 
                        id="sexoMasculino" 
                        v-model="formData.Sex" 
                        value="M" 
                        type="radio" 
                        required
                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <label for="sexoMasculino" class="ml-2 block text-sm text-gray-700">Masculino</label>
                    </div>
                    <div class="flex items-center">
                      <input 
                        id="sexoFemenino" 
                        v-model="formData.Sex" 
                        value="F" 
                        type="radio" 
                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <label for="sexoFemenino" class="ml-2 block text-sm text-gray-700">Femenino</label>
                    </div>
                  </div>
                </div>
                
                <div class="hidden">
                  <label class="block text-sm font-medium text-gray-500">¿Trabaja actualmente?</label>
                  <div class="mt-2 space-x-6 flex">
                    <div class="flex items-center">
                      <input 
                        id="trabajaSi" 
                        v-model="trabajaActualmente" 
                        :value="true" 
                        type="radio"
                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <label for="trabajaSi" class="ml-2 block text-sm text-gray-700">Sí</label>
                    </div>
                    <div class="flex items-center">
                      <input 
                        id="trabajaNo" 
                        v-model="trabajaActualmente" 
                        :value="false" 
                        type="radio" 
                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <label for="trabajaNo" class="ml-2 block text-sm text-gray-700">No</label>
                    </div>
                  </div>
                </div>

                <div v-if="trabajaActualmente" class="hidden">
                  <label for="trabajo" class="block text-sm font-medium text-gray-500">Lugar de trabajo</label>
                  <select 
                    id="trabajo" 
                    v-model="formData.WorkId"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar trabajo</option>
                    <option v-for="trabajo in trabajos" :key="trabajo.WorkId" :value="trabajo.WorkId">{{ trabajo.WorkName }}</option>
                  </select>
                </div>

                <div class="hidden">
                  <label for="escuela" class="block text-sm font-medium text-gray-500">Escuela de procedencia*</label>
                  <input 
                    id="escuela" 
                    v-model="formData.SchoolId" 
                    type="text" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            
            <!-- Lugar de origen -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6 hidden">
              <h2 class="text-lg font-semibold mb-4">Lugar de origen</h2>
              
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label for="paisOrigen" class="block text-sm font-medium text-gray-500">País de origen*</label>
                  <select 
                    id="paisOrigen" 
                    v-model="formData.OriginCountryId" 
                    @change="loadEstadosOrigen"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar país</option>
                    <option v-for="pais in paises" :key="pais.CountryId" :value="pais.CountryId">{{ pais.Country.toUpperCase() }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="estadoOrigen" class="block text-sm font-medium text-gray-500">Estado de origen*</label>
                  <select 
                    id="estadoOrigen" 
                    v-model="formData.OriginStateId" 
                    @change="loadCiudadesOrigen"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar estado</option>
                    <option v-for="estado in estadosOrigen" :key="estado.StateId" :value="estado.StateId">{{ estado.State }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="ciudadOrigen" class="block text-sm font-medium text-gray-500">Ciudad de origen*</label>
                  <select 
                    id="ciudadOrigen" 
                    v-model="formData.OriginCityId" 
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar ciudad</option>
                    <option v-for="ciudad in ciudadesOrigen" :key="ciudad.CityId" :value="ciudad.CityId">{{ ciudad.City }}</option>
                  </select>
                </div>
              </div>
            </div>
            
            <!-- Domicilio -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
              <h2 class="text-lg font-semibold mb-4">Domicilio actual</h2>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="paisResidencia" class="block text-sm font-medium text-gray-500">País donde reside*</label>
                  <select 
                    id="paisResidencia" 
                    v-model="formData.ActualCountryId" 
                    required
                    @change="loadEstadosActual"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar país</option>
                    <option v-for="pais in paises" :key="pais.CountryId" :value="pais.CountryId">{{ pais.Country.toUpperCase() }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="estado" class="block text-sm font-medium text-gray-500">Estado*</label>
                  <select 
                    id="estado" 
                    v-model="formData.ActualStateId" 
                    required
                    @change="loadCiudadesActual"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar estado</option>
                    <option v-for="estado in estadosActual" :key="estado.StateId" :value="estado.StateId">{{ estado.State }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="ciudad" class="block text-sm font-medium text-gray-500">Ciudad*</label>
                  <select 
                    id="ciudad" 
                    v-model="formData.ActualCityId" 
                    required
                    @change="loadColonias"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar ciudad</option>
                    <option v-for="ciudad in ciudadesActual" :key="ciudad.CityId" :value="ciudad.CityId">{{ ciudad.City }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="colonia" class="block text-sm font-medium text-gray-500">Colonia*</label>
                  <select 
                    id="colonia" 
                    v-model="formData.ActualColonyId" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar colonia</option>
                    <option v-for="colonia in colonias" :key="colonia.ColonyId" :value="colonia.ColonyId">{{ colonia.Colony }}</option>
                  </select>
                </div>
                
                <div class="md:col-span-2">
                  <label for="calleNumero" class="block text-sm font-medium text-gray-500">Calle y número*</label>
                  <input 
                    id="calleNumero" 
                    v-model="formData.Street" 
                    type="text" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Ej: Av. Universidad #123"
                  />
                </div>
              </div>
            </div>
            
            <!-- Información académica -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
              <h2 class="text-lg font-semibold mb-4">Información académica</h2>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="instituto" class="block text-sm font-medium text-gray-500">Instituto*</label>
                  <select 
                    id="instituto" 
                    v-model="formData.InstituteId" 
                    required
                    @change="loadCampus"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar instituto</option>
                    <option v-for="instituto in institutos" :key="instituto.InstituteId" :value="instituto.InstituteId">{{ instituto.InstituteName.toUpperCase() }}</option>
                  </select>
                </div>

                <div class="hidden">
                  <label for="institutoProp" class="block text-sm font-medium text-gray-500">Propiedad de Instituto*</label>
                  <select 
                    id="institutoProp" 
                    v-model="formData.InstituteProperty" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar propiedad</option>
                    <option value="Propio" selected>Propio</option>
                    <option value="Incorporado">Incorporado</option>
                  </select>
                </div>
                
                <div>
                  <label for="campus" class="block text-sm font-medium text-gray-500">Campus*</label>
                  <select 
                    id="campus" 
                    v-model="formData.CampusId" 
                    required
                    @change="loadPeriodos"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar campus</option>
                    <option v-for="campus in campuses" :key="campus.CampusId" :value="campus.CampusId">{{ campus.CampusName }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="periodo" class="block text-sm font-medium text-gray-500">Periodo*</label>
                  <select 
                    id="periodo" 
                    v-model="periodoSeleccionado" 
                    required
                    @change="loadCarreras"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar periodo</option>
                    <option v-for="periodo in periodos" :key="periodo.PeriodUda" :value="periodo.PeriodUda">{{ periodo.Description.toUpperCase() }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="carrera" class="block text-sm font-medium text-gray-500">Carrera*</label>
                  <select 
                    id="carrera" 
                    v-model="formData.CareerId" 
                    required
                    @change="loadTurnos"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar carrera</option>
                    <option v-for="carrera in carreras" :key="carrera.CareerId" :value="carrera.CareerId">{{ carrera.CareerName }}</option>
                  </select>
                </div>
                
                <div>
                  <label for="turno" class="block text-sm font-medium text-gray-500">Turno*</label>
                  <select 
                    id="turno" 
                    v-model="formData.ShiftId" 
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar turno</option>
                    <option v-for="turno in turnos" :key="turno.ShiftId" :value="turno.ShiftId">{{ turno.ShiftName.toUpperCase() }}</option>
                  </select>
                </div>
              </div>
            </div>
            
            <!-- Información adicional -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white p-6 mb-6">
              <h2 class="text-lg font-semibold mb-4">Información adicional</h2>
              
              <div class="grid grid-cols-1 gap-6">
                <div class="hidden">
                  <label for="actividadAdicional" class="block text-sm font-medium text-gray-500">Actividad adicional</label>
                  <input 
                    id="actividadAdicional" 
                    v-model="formData.ActivityId" 
                    type="number" 
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label for="comentarios" class="block text-sm font-medium text-gray-500">Comentarios</label>
                  <textarea 
                    id="comentarios" 
                    v-model="formData.Comments" 
                    rows="4" 
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Información relevante sobre el prospecto..."
                  ></textarea>
                </div>
              </div>
            </div>
            
            <!-- Botones de acción -->
            <div class="flex flex-col sm:flex-row gap-3 justify-end">
              <button 
                type="button"
                class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                @click="resetForm"
              >
                <svg class="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v6h6M20 20v-6h-6M4 14a8 8 0 0114-5.3M20 10a8 8 0 01-14 5.3" />
                </svg>
                Limpiar formulario
              </button>
              <button 
                type="submit"
                class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                :disabled="loading"
              >
                  <svg v-if="!loading" class="w-4 h-4 mr-2 text-white" fill="none" stroke="currentColor"
                    stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7" />
                  </svg>
                {{ loading ? 'Enviando...' : 'Registrar prospecto' }}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import {
  getSchools,
  getWorks,
  getCountries,
  getStates,
  getCities,
  getColonies,
  getInstitutesByStudent,
  getCampus,
  getProspectPeriods,
  getCareers,
  getShifts,
  createProspect,
  createProspectPeriod,
  getStudentData
} from '@/services/api.js';

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      loading: false,
      loadingData: false,
      successMessage: null,
      errorMessage: null,
      trabajaActualmente: false,
      periodoSeleccionado: '',
      adviserName: '',
      phoneErrors: {
        Phone: null,
        Mobile: null
      },
      emailError: null,
      // Arrays existentes
      escuelas: [],
      trabajos: [],
      paises: [],
      estadosOrigen: [],
      ciudadesOrigen: [],
      estadosActual: [],
      ciudadesActual: [],
      colonias: [],
      institutos: [],
      campuses: [],
      periodos: [],
      carreras: [],
      turnos: [],
      formData: {
        InstituteProperty: "Propio",
        ActivityId: 0,
        Names: "",
        FatherLastName: "",
        MotherLastName: "",
        Curp: "",
        Email: "",
        Birthdate: "",
        Sex: "",
        Phone: "",
        Mobile: "",
        SchoolId: 4520,
        WorkId: "",
        OriginCountryId: "",
        OriginStateId: "",
        OriginCityId: "",
        ActualCountryId: "",
        ActualStateId: "",
        ActualCityId: "",
        ActualColonyId: "",
        Street: "",
        InstituteId: "",
        CampusId: "",
        CareerId: "",
        ShiftId: "",
        Comments: "",
        UserName: ""
      }
    };
  },
  async created() {
    await this.getUserFromBackend();
    if (this.user) {
      this.formData.UserName = this.user.username || "";
      await this.loadAdviserName();
      await this.loadInitialData();
    }
  },
  methods: {
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.user.username = this.user.email.match(/^a(\d+)@/)[1];
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },
    async loadAdviserName() {
      try {
        const response = await getStudentData(this.user.username);
        if (response.data[0] && response.data[0].Adviser) {
          this.adviserName = response.data[0].Adviser;
        } else {
          console.log("No se encontró el campo Adviser en la respuesta");
        }
      } catch (error) {
        console.error("Error cargando nombre del asesor:", error);
      }
    },
    async loadInitialData() {
      this.loadingData = true;
      try {
        await Promise.all([
          this.loadEscuelas(),
          this.loadTrabajos(),
          this.loadPaises(),
          this.loadInstitutos()
        ]);
      } catch (error) {
        console.error("Error cargando datos iniciales:", error);
        this.errorMessage = "Error cargando datos del formulario. Por favor, intente nuevamente.";

        await this.$alert(
            'Error cargando datos del formulario',
            'Por favor, intente nuevamente.',
            'error'
        );
      } finally {
        this.loadingData = false;
      }
    },
    async loadEscuelas() {
      try {
        const response = await getSchools();
        this.escuelas = response.data || [];
      } catch (error) {
        console.error("Error cargando escuelas:", error);
        throw error;
      }
    },
    async loadTrabajos() {
      try {
        const response = await getWorks();
        this.trabajos = response.data || [];
      } catch (error) {
        console.error("Error cargando trabajos:", error);
        throw error;
      }
    },
    async loadPaises() {
      try {
        const response = await getCountries();
        this.paises = response.data || [];
      } catch (error) {
        console.error("Error cargando países:", error);
        throw error;
      }
    },
    async loadEstadosOrigen() {
      if (!this.formData.OriginCountryId) return;
      
      try {
    const response = await getStates(this.formData.OriginCountryId);
    this.estadosOrigen = response.data || [];
    this.formData.OriginStateId = ""; // Reset estado
    this.formData.OriginCityId = ""; // Reset ciudad
  } catch (error) {
    console.error("Error cargando estados de origen:", error);
  }
},
async loadCiudadesOrigen() {
  if (!this.formData.OriginStateId) return;
  
  try {
    const response = await getCities(this.formData.OriginStateId);
    this.ciudadesOrigen = response.data || [];
    this.formData.OriginCityId = ""; // Reset ciudad
  } catch (error) {
    console.error("Error cargando ciudades de origen:", error);
  }
},

async loadEstadosActual() {
  if (!this.formData.ActualCountryId) return;
  
  try {
    const response = await getStates(this.formData.ActualCountryId);
    this.estadosActual = response.data || [];
    this.formData.ActualStateId = ""; // Reset estado
    this.formData.ActualCityId = ""; // Reset ciudad
    this.formData.ActualColonyId = ""; // Reset colonia
  } catch (error) {
    console.error("Error cargando estados actuales:", error);
  }
},

async loadCiudadesActual() {
  if (!this.formData.ActualStateId) return;
  
  try {
    const response = await getCities(this.formData.ActualStateId);
    this.ciudadesActual = response.data || [];
    this.formData.ActualCityId = ""; // Reset ciudad
    this.formData.ActualColonyId = ""; // Reset colonia
  } catch (error) {
    console.error("Error cargando ciudades actuales:", error);
  }
},

async loadColonias() {
  if (!this.formData.ActualCityId) return;
  
  try {
    const response = await getColonies(this.formData.ActualCityId);
    this.colonias = response.data || [];
    this.formData.ActualColonyId = ""; // Reset colonia
  } catch (error) {
    console.error("Error cargando colonias:", error);
  }
},

async loadInstitutos() {
  try {
    const response = await getInstitutesByStudent(this.user.username);
    this.institutos = response.data || [];
  } catch (error) {
    console.error("Error cargando institutos:", error);
    throw error;
  }
},

async loadCampus() {
  if (!this.formData.InstituteId) return;
  
  try {
    const response = await getCampus(this.formData.InstituteId);
    this.campuses = response.data || [];
    this.formData.CampusId = ""; // Reset campus
    this.periodoSeleccionado = ""; // Reset periodo
    this.formData.CareerId = ""; // Reset carrera
    this.formData.ShiftId = ""; // Reset turno
  } catch (error) {
    console.error("Error cargando campus:", error);
  }
},

async loadPeriodos() {
  if (!this.formData.CampusId) return;
  
  try {
    const response = await getProspectPeriods(this.formData.CampusId);
    this.periodos = response.data || [];
    this.periodoSeleccionado = ""; // Reset periodo
    this.formData.CareerId = ""; // Reset carrera
    this.formData.ShiftId = ""; // Reset turno
  } catch (error) {
    console.error("Error cargando periodos:", error);
  }
},

async loadCarreras() {
  if (!this.periodoSeleccionado || !this.formData.CampusId) return;
  
  try {
    const response = await getCareers(this.formData.CampusId, this.periodoSeleccionado);
    this.carreras = response.data || [];
    this.formData.CareerId = ""; // Reset carrera
    this.formData.ShiftId = ""; // Reset turno
  } catch (error) {
    console.error("Error cargando carreras:", error);
  }
},

async loadTurnos() {
  if (!this.formData.CareerId || !this.periodoSeleccionado || !this.formData.CampusId) return;
  
  try {
    const response = await getShifts(this.formData.CampusId, this.periodoSeleccionado, this.formData.CareerId);
    this.turnos = response.data || [];
    this.formData.ShiftId = ""; // Reset turno
  } catch (error) {
    console.error("Error cargando turnos:", error);
  }
},

validatePhoneInput(field) {
  // Remover caracteres no numéricos
  this.formData[field] = this.formData[field].replace(/[^0-9]/g, '');
  
  // Limitar a 10 dígitos
  if (this.formData[field].length > 10) {
    this.formData[field] = this.formData[field].slice(0, 10);
  }
  
  // Limpiar error si existe
  if (this.phoneErrors[field]) {
    this.phoneErrors[field] = null;
  }
},

validatePhone(field) {
  const phone = this.formData[field];
  
  // Si está vacío y es teléfono móvil (requerido)
  if (!phone && field === 'Mobile') {
    this.phoneErrors[field] = 'El teléfono móvil es obligatorio';
    return false;
  }
  
  // Si no está vacío, validar formato
  if (phone && phone.length !== 10) {
    this.phoneErrors[field] = 'Por favor, ingrese un teléfono de 10 dígitos.';
    return false;
  }
  
  // Si no está vacío, validar que solo contenga números
  if (phone && !/^\d{10}$/.test(phone)) {
    this.phoneErrors[field] = 'El teléfono debe contener solo números';
    return false;
  }
  
  // Limpiar error si la validación es exitosa
  this.phoneErrors[field] = null;
  return true;
},

validateEmail() {
  const email = this.formData.Email;
  
  // Si está vacío, es válido (ya no es requerido)
  if (!email) {
    this.emailError = null;
    return true;
  }
  
  // Validar formato de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    this.emailError = 'Por favor, ingrese un email válido.';
    return false;
  }
  
  // Limpiar error si la validación es exitosa
  this.emailError = null;
  return true;
},

validateForm() {
  let isValid = true;
  
  // Validar teléfono móvil (obligatorio)
  if (!this.validatePhone('Mobile')) {
    isValid = false;
  }
  
  // Validar teléfono fijo (solo si tiene valor)
  if (this.formData.Phone && !this.validatePhone('Phone')) {
    isValid = false;
  }
  
  // Validar email
  if (!this.validateEmail()) {
    isValid = false;
  }
  
  return isValid;
},

async submitForm() {
  this.successMessage = null;
  this.errorMessage = null;
  
  try {
    this.loading = true;
    
    // Reiniciar mensajes previos
    this.successMessage = '';
    this.errorMessage = '';

    // Validar formulario completo
    if (!this.validateForm()) {
      throw new Error("Por favor corrija los errores en el formulario");
    }

    // Validar que los campos obligatorios estén completos
    if (!this.formData.Names || !this.formData.FatherLastName || !this.formData.MotherLastName) {
        throw new Error("Debe completar todos los campos obligatorios");
    }

    // Establecer campos ocultos como cadenas vacías
    this.formData.Phone = "";
    this.formData.WorkId = "";
    this.formData.OriginCountryId = "";
    this.formData.OriginStateId = "";
    this.formData.OriginCityId = "";

    // Insertar prospecto primero
    const prospectoResponse = await createProspect(this.formData);

    // Extraer el ID del prospecto directamente de Message
    const prospectId = prospectoResponse.data.Message;

    // Validación adicional
    if (!prospectId) {
        throw new Error("No se pudo obtener el ID del prospecto");
    }

    // Insertar periodo del prospecto si está seleccionado
    if (this.periodoSeleccionado) {
        await createProspectPeriod({
            PeriodId: this.periodoSeleccionado,
            ProspectId: prospectId
        });
    }

    // Configurar mensaje de éxito de manera inmediata
    this.$nextTick(() => {
        this.successMessage = 'El prospecto ha sido registrado exitosamente.';
    });

    await this.$alert(
        'Prospecto registrado exitosamente',
        '',
        'success'
      );

    // Resetear el formulario
    this.resetForm();

} catch (error) {
    console.error('Error al registrar prospecto:', error);
    
    // Configurar mensaje de error de manera inmediata
    this.$nextTick(() => {
        this.errorMessage = error.response?.data?.message || 
                            error.message || 
                            'Ocurrió un error al registrar el prospecto. Por favor intente nuevamente.';
    });

    await this.$alert(
        'Error al registrar el prospecto',
        'Por favor, intente nuevamente.',
        'error'
    );
} finally {
    this.loading = false;
}
},

resetForm() {
  // Reiniciar el formulario a valores iniciales
  this.formData = {
    InstituteProperty: "Propio",
    ActivityId: 0,
    Names: "",
    FatherLastName: "",
    MotherLastName: "",
    Curp: "",
    Email: "",
    Birthdate: "",
    Sex: "",
    Phone: "",
    Mobile: "",
    SchoolId: 4520,
    WorkId: "",
    OriginCountryId: "",
    OriginStateId: "",
    OriginCityId: "",
    ActualCountryId: "",
    ActualStateId: "",
    ActualCityId: "",
    ActualColonyId: "",
    Street: "",
    InstituteId: "",
    CampusId: "",
    CareerId: "",
    ShiftId: "",
    Comments: "",
    UserName: this.user?.username || ""
  };
  this.trabajaActualmente = false;
  this.periodoSeleccionado = "";
  
  // Limpiar errores de validación
  this.phoneErrors = {
    Phone: null,
    Mobile: null
  };
  this.emailError = null;
  
  // Limpiamos los mensajes
  this.successMessage = null;
  this.errorMessage = null;
},
toggleMenu() {
  this.isMenuOpen = !this.isMenuOpen;
},
closeMenu() {
  this.isMenuOpen = false;
},
async logout() {
  try {
    // Obtener nuevo token CSRF
    await axios.get('/sanctum/csrf-cookie');
    
    // Esperar un momento para asegurar que la cookie se haya establecido
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Realizar la petición de logout
    const response = await axios.post('/logout', {}, {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    console.log(response);
    // Limpiar cookies locales
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Redirigir al login
    this.$router.push('/login');
  } catch (error) {
    console.error('Error al cerrar sesión:', error.response?.data || error);
  }
}
  }
};
</script>